import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Briefcase, 
  Users, 
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Heart
} from 'lucide-react';
import { 
  useJobTitles, 
  useRelationshipTypes,
  useCreateJobTitle,
  useUpdateJobTitle,
  useDeleteJobTitle,
  useCreateRelationshipType,
  useUpdateRelationshipType,
  useDeleteRelationshipType,
  useReorderJobTitles,
  useReorderRelationshipTypes,
  useSeedJobTitles,
  useSeedRelationshipTypes
} from '../../hooks/useTitles';
import JobTitleForm from '../../components/titles/JobTitleForm';
import RelationshipTypeForm from '../../components/titles/RelationshipTypeForm';

// Default colors for visual distinction
const COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#F97316', label: 'Orange' },
  { value: '#6B7280', label: 'Gray' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#A855F7', label: 'Violet' },
];

// Default job titles to seed
const DEFAULT_JOB_TITLES = [
  { name: 'Teacher', description: 'Primary classroom instructor', color: '#3B82F6' },
  { name: 'Administrator', description: 'School administrator', color: '#8B5CF6' },
  { name: 'Director', description: 'Program or school director', color: '#DC2626' },
  { name: 'Office Manager', description: 'Office and administrative management', color: '#10B981' },
  { name: 'Accountant', description: 'Financial and bookkeeping', color: '#F59E0B' },
  { name: 'Receptionist', description: 'Front desk and reception', color: '#06B6D4' },
];

// Default relationship types to seed
const DEFAULT_RELATIONSHIP_TYPES = [
  { name: 'Mother', description: 'Biological or adoptive mother', isPrimary: true, isEmergencyContact: true },
  { name: 'Father', description: 'Biological or adoptive father', isPrimary: true, isEmergencyContact: true },
  { name: 'Guardian', description: 'Legal guardian', isPrimary: true, isEmergencyContact: true },
  { name: 'Stepmother', description: 'Stepmother', isPrimary: false, isEmergencyContact: true },
  { name: 'Stepfather', description: 'Stepfather', isPrimary: false, isEmergencyContact: true },
  { name: 'Grandmother', description: 'Paternal or maternal grandmother', isPrimary: false, isEmergencyContact: true },
  { name: 'Grandfather', description: 'Paternal or maternal grandfather', isPrimary: false, isEmergencyContact: true },
  { name: 'Other', description: 'Other relationship', isPrimary: false, isEmergencyContact: true },
];

const TitleSettings = () => {
  const [activeTab, setActiveTab] = useState('job_titles');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [notification, setNotification] = useState(null);

  // Fetch data
  const { data: jobTitlesData, isLoading: isLoadingJobTitles, refetch: refetchJobTitles } = useJobTitles(true);
  const { data: relationshipTypesData, isLoading: isLoadingRelationshipTypes, refetch: refetchRelationshipTypes } = useRelationshipTypes(true);

  // Mutations
  const createJobTitle = useCreateJobTitle();
  const updateJobTitle = useUpdateJobTitle();
  const deleteJobTitle = useDeleteJobTitle();
  const reorderJobTitles = useReorderJobTitles();
  // eslint-disable-next-line no-unused-vars
  const _seedJobTitles = useSeedJobTitles();
  
  const createRelationshipType = useCreateRelationshipType();
  const updateRelationshipType = useUpdateRelationshipType();
  const deleteRelationshipType = useDeleteRelationshipType();
  const reorderRelationshipTypes = useReorderRelationshipTypes();
  // eslint-disable-next-line no-unused-vars
  const _seedRelationshipTypes = useSeedRelationshipTypes();

  // Extract data - wrapped in useMemo to avoid recreating references on every render
  const jobTitles = useMemo(() => jobTitlesData?.data || [], [jobTitlesData?.data]);
  const relationshipTypes = useMemo(() => relationshipTypesData?.data || [], [relationshipTypesData?.data]);

  // Update local items when data changes
  useEffect(() => {
    if (activeTab === 'job_titles') {
      setLocalItems([...jobTitles].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
    } else {
      setLocalItems([...relationshipTypes].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
    }
  }, [jobTitles, relationshipTypes, activeTab]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle add new
  const handleAdd = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (item) => {
    const itemType = activeTab === 'job_titles' ? 'job title' : 'relationship type';
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?\n\nThis may affect users currently assigned to this ${itemType}.`)) {
      return;
    }
    
    try {
      if (activeTab === 'job_titles') {
        await deleteJobTitle.mutateAsync(item.id);
      } else {
        await deleteRelationshipType.mutateAsync(item.id);
      }
      showNotification(`${item.name} deleted successfully`);
    } catch (err) {
      console.error('Failed to delete:', err);
      showNotification(err.message || 'Failed to delete', 'error');
    }
  };

  // Handle form submit
  const handleFormSubmit = async (formData) => {
    try {
      if (activeTab === 'job_titles') {
        if (editingItem) {
          await updateJobTitle.mutateAsync({ id: editingItem.id, data: formData });
          showNotification('Job title updated successfully');
        } else {
          await createJobTitle.mutateAsync({ ...formData, displayOrder: localItems.length });
          showNotification('Job title created successfully');
        }
      } else {
        if (editingItem) {
          await updateRelationshipType.mutateAsync({ id: editingItem.id, data: formData });
          showNotification('Relationship type updated successfully');
        } else {
          await createRelationshipType.mutateAsync({ ...formData, displayOrder: localItems.length });
          showNotification('Relationship type created successfully');
        }
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Failed to save:', err);
      showNotification(err.message || 'Failed to save', 'error');
    }
  };

  // Handle reorder (move up/down)
  const handleMove = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= localItems.length) return;

    const newItems = [...localItems];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update local state immediately for responsive UI
    setLocalItems(newItems);

    // Update display order for all items
    const orderedIds = newItems.map(item => item.id);
    
    try {
      if (activeTab === 'job_titles') {
        await reorderJobTitles.mutateAsync(orderedIds);
      } else {
        await reorderRelationshipTypes.mutateAsync(orderedIds);
      }
    } catch (err) {
      console.error('Failed to reorder:', err);
      // Revert on error
      setLocalItems([...localItems]);
      showNotification('Failed to reorder items', 'error');
    }
  };

  // Handle seed defaults
  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default titles to your list. Continue?')) return;

    try {
      if (activeTab === 'job_titles') {
        // Create each default job title
        for (const title of DEFAULT_JOB_TITLES) {
          const exists = jobTitles.some(jt => jt.name.toLowerCase() === title.name.toLowerCase());
          if (!exists) {
            await createJobTitle.mutateAsync(title);
          }
        }
        await refetchJobTitles();
        showNotification('Default job titles added');
      } else {
        // Create each default relationship type
        for (const type of DEFAULT_RELATIONSHIP_TYPES) {
          const exists = relationshipTypes.some(rt => rt.name.toLowerCase() === type.name.toLowerCase());
          if (!exists) {
            await createRelationshipType.mutateAsync(type);
          }
        }
        await refetchRelationshipTypes();
        showNotification('Default relationship types added');
      }
    } catch (err) {
      console.error('Failed to seed:', err);
      showNotification(err.message || 'Failed to add defaults', 'error');
    }
  };

  // Get color label
  const getColorLabel = (colorValue) => {
    const color = COLORS.find(c => c.value.toLowerCase() === colorValue?.toLowerCase());
    return color?.label || 'Custom';
  };

  // Render loading state
  const isLoading = activeTab === 'job_titles' ? isLoadingJobTitles : isLoadingRelationshipTypes;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Title Settings</h1>
        <p className="text-gray-500 mt-1">
          Customize job titles for employees and relationship types for parents
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-4 p-4 rounded-lg flex items-center space-x-2 ${
          notification.type === 'error' 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {notification.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('job_titles')}
          className={`flex items-center space-x-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'job_titles'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Briefcase size={18} />
          <span>Job Titles (Employees)</span>
        </button>
        <button
          onClick={() => setActiveTab('relationship_types')}
          className={`flex items-center space-x-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'relationship_types'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Users size={18} />
          <span>Relationship Types (Parents)</span>
        </button>
      </div>

      {/* Description */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-700">
          {activeTab === 'job_titles' 
            ? 'Job titles help organize your employees by role. Each employee can be assigned a job title for easy identification and reporting.'
            : 'Relationship types define how parents/guardians are related to students. This helps identify primary contacts and emergency contacts.'
          }
        </p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleAdd}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus size={18} />
            <span>Add {activeTab === 'job_titles' ? 'Job Title' : 'Relationship Type'}</span>
          </button>
          
          {localItems.length === 0 && (
            <button
              onClick={handleSeedDefaults}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw size={18} />
              <span>Use Defaults</span>
            </button>
          )}
        </div>

        <div className="text-sm text-gray-500">
          {isLoading ? (
            'Loading...'
          ) : (
            <>
              <span className="font-medium text-gray-900">{localItems.length}</span> {activeTab === 'job_titles' ? 'job titles' : 'relationship types'} defined
              {activeTab === 'job_titles' && jobTitles.filter(j => j.isActive === false).length > 0 && (
                <span className="ml-2">({jobTitles.filter(j => j.isActive === false).length} inactive)</span>
              )}
              {activeTab === 'relationship_types' && relationshipTypes.filter(r => r.isActive === false).length > 0 && (
                <span className="ml-2">({relationshipTypes.filter(r => r.isActive === false).length} inactive)</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                {activeTab === 'job_titles' ? (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Color
                  </th>
                ) : (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : localItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      {activeTab === 'job_titles' ? (
                        <Briefcase size={48} className="mb-4 text-gray-300" />
                      ) : (
                        <Users size={48} className="mb-4 text-gray-300" />
                      )}
                      <p className="text-lg font-medium text-gray-900 mb-1">
                        No {activeTab === 'job_titles' ? 'job titles' : 'relationship types'} yet
                      </p>
                      <p className="text-sm mb-4">
                        Get started by adding your first {activeTab === 'job_titles' ? 'job title' : 'relationship type'} or use the defaults
                      </p>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={handleAdd}
                          className="btn-primary flex items-center space-x-2"
                        >
                          <Plus size={18} />
                          <span>Add New</span>
                        </button>
                        <button
                          onClick={handleSeedDefaults}
                          className="btn-secondary flex items-center space-x-2"
                        >
                          <RefreshCw size={18} />
                          <span>Use Defaults</span>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                localItems.map((item, index) => (
                  <tr 
                    key={item.id} 
                    className={`hover:bg-gray-50 transition-colors ${item.isActive === false ? 'opacity-60' : ''}`}
                  >
                    {/* Order */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center space-y-1">
                        <span className="text-xs font-medium text-gray-400">{index + 1}</span>
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleMove(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-gray-400 hover:text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            onClick={() => handleMove(index, 'down')}
                            disabled={index === localItems.length - 1}
                            className="p-0.5 text-gray-400 hover:text-purple-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ArrowDown size={14} />
                          </button>
                        </div>
                      </div>
                    </td>

                    {/* Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        {activeTab === 'job_titles' ? (
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: item.color || '#6B7280' }}
                          >
                            <Briefcase size={16} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Heart size={16} className="text-purple-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.isSystem && (
                            <span className="text-xs text-gray-400">System default</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-600 max-w-xs truncate">
                        {item.description || '-'}
                      </p>
                    </td>

                    {/* Color / Permissions */}
                    <td className="px-4 py-3">
                      {activeTab === 'job_titles' ? (
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-200"
                            style={{ backgroundColor: item.color || '#6B7280' }}
                          />
                          <span className="text-sm text-gray-600">{getColorLabel(item.color)}</span>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {item.isPrimary && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              Primary
                            </span>
                          )}
                          {item.isEmergencyContact && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              Emergency
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.isActive !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          {activeTab === 'job_titles' ? (
            <JobTitleForm
              initialData={editingItem}
              colors={COLORS}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
              }}
            />
          ) : (
            <RelationshipTypeForm
              initialData={editingItem}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TitleSettings;
